import type { LeadForm, LeadFormInputField } from '@/types/lead-forms';

/** Client-safe normalization for lead-form data already fetched on the server. */
export function normalizeLeadForms(forms: LeadForm[]): LeadForm[] {
  return forms.map((form) => ({
    ...form,
    nameContact: form.nameContact ?? [],
    forms: (form.forms ?? []).map((variant) => ({
      ...variant,
      inputType: variant.inputType ?? [],
      stateDistrictDropdown: variant.stateDistrictDropdown ?? [],
      selectType: (variant.selectType ?? []).map((field) => ({
        ...field,
        options: field.options ?? [],
      })),
      textArea: variant.textArea ?? [],
      radioButton: variant.radioButton ?? [],
      checkbox: variant.checkbox ?? [],
    })),
  }));
}

/** CMS labels often bake in `*` — strip so the UI can render a red asterisk from `required`. */
export function stripFieldLabelAsterisk(label: string | null | undefined): string {
  return (label ?? '').replace(/\s*\*+\s*$/g, '').trim();
}

/**
 * Strict: only `required: true` from the API marks a field required.
 * `null` / `false` / missing → optional (no asterisk, not blocking submit).
 */
export function isLeadFieldRequired(field: { required?: boolean | null }): boolean {
  return field.required === true;
}

/** Detect phone-ish nameContact / input rows (CMS uses both Phone Number and Number). */
export function isLeadPhoneField(
  field: Pick<LeadFormInputField, 'inputType' | 'inputId' | 'label'>,
): boolean {
  if (field.inputType === 'Phone Number') return true;
  const haystack = `${field.inputId ?? ''} ${field.label ?? ''}`.toLowerCase();
  return /phone|mobile/.test(haystack);
}

/** Detect the primary "full name" text field in nameContact. */
export function isLeadNameField(
  field: Pick<LeadFormInputField, 'inputType' | 'inputId' | 'label'>,
): boolean {
  if (field.inputType !== 'Text') return false;
  const haystack = `${field.inputId ?? ''} ${field.label ?? ''}`.toLowerCase();
  return /\bname\b/.test(haystack) && !/organization|dealership|business/.test(haystack);
}
